import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  #cartSyncRequestId = 0;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent || '0', 10) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from BFCache.
   *
   * BFCache (Back-Forward Cache) restores the page from a snapshot, meaning
   * the Liquid-rendered cart count may be stale. We fix this by silently
   * fetching the live cart state from /cart.js and updating the badge.
   *
   * The old approach (sessionStorage + 10s window) could still show stale
   * counts after the window expired. A live fetch is the only reliable fix.
   *
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (!event.persisted) return;

    this.syncLiveCartCount(false);
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const data = event.detail && event.detail.data;
    const itemCount = data && data.itemCount !== undefined ? data.itemCount : null;
    const comingFromProductForm = data && data.source === 'product-form-component';

    if (itemCount !== null) {
      this.renderCartBubble(itemCount, comingFromProductForm);
      return;
    }

    this.syncLiveCartCount(false);
  };

  /**
   * Fetches the live Shopify cart count and reconciles the header badge.
   * This prevents stale badge counts after rapid cart edits or back navigation.
   *
   * @param {boolean} animate - Whether to animate the bubble.
   */
  syncLiveCartCount = (animate = false) => {
    const requestId = ++this.#cartSyncRequestId;

    fetch(`${Theme.routes.cart_url}.js`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
    })
      .then((response) => response.json())
      .then((cart) => {
        if (requestId !== this.#cartSyncRequestId) return;

        const count = (cart && cart.item_count) !== undefined ? cart.item_count : 0;
        this.renderCartBubble(count, false, animate);
      })
      .catch(() => {
        this.ensureCartBubbleIsCorrect();
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   * @param {boolean} animate - Whether to animate the bubble.
   */
  renderCartBubble = (itemCount, comingFromProductForm, animate = true) => {
    // ── 1. Update the count INSTANTLY (sync, no await) ──────────────────
    // This is the most important part — the number must update before the
    // next paint, not after an animation frame or promise resolution.
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = comingFromProductForm
      ? this.currentCartCount + itemCount
      : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    // ── 2. Run the bounce animation independently (non-blocking) ─────────
    // requestAnimationFrame ensures the DOM has updated before we add the
    // animation class, but we do NOT await it — the count is already correct.
    if (!animate || itemCount === 0) return;

    requestAnimationFrame(() => {
      this.refs.cartBubble.classList.add('cart-bubble--animating');

      // Remove animation class after it finishes — use transitionend instead of
      // the blocking onAnimationEnd() helper to keep this non-blocking.
      const cleanup = () => {
        this.refs.cartBubble.classList.remove('cart-bubble--animating');
        this.refs.cartBubbleText.removeEventListener('animationend', cleanup);
      };
      this.refs.cartBubbleText.addEventListener('animationend', cleanup, { once: true });
    });
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
